import type { CSSProperties, ReactNode } from 'react';
import type { Block } from '../types';
import { getDefinition } from './catalog';

/* ==========================================================================
   Tree -> React renderer.
   --------------------------------------------------------------------------
   Renders the editing canvas. The same JSX is used both for editing
   (with overlays / selection rings) and preview / print mode.

   Each element gets a `data-block-id` so the canvas can find what was
   clicked. Container blocks render their children recursively.

   Keep visual concerns thin -- the actual paper, fonts, and theme tokens
   come from the existing print.css + themes/*.css that ship in /public.
   ========================================================================== */

interface RenderProps {
  block: Block;
  selectedId: string | null;
  mode: 'edit' | 'preview';
  onSelect?: (id: string) => void;
  /** Edit-mode hooks for inline-editing rich text blocks. */
  onPropChange?: (id: string, key: string, value: unknown) => void;
  /** Optional node injector used by the canvas to mount Tiptap into rich blocks. */
  renderRich?: (block: Block, key: string, html: string) => ReactNode;
}

function getProp<T>(block: Block, key: string, fallback: T): T {
  const v = block.props?.[key];
  return (v === undefined || v === null) ? fallback : (v as T);
}

function selectionAttrs(block: Block, mode: 'edit' | 'preview', selectedId: string | null) {
  const isSelected = mode === 'edit' && selectedId === block.id;
  const className = mode === 'edit'
    ? `cb-block ${isSelected ? 'is-selected' : ''}`
    : '';
  return {
    'data-block-id': block.id,
    'data-component': block.type,
    className,
  };
}

function NoOpRich({ html }: { html: string }) {
  return <div dangerouslySetInnerHTML={{ __html: html || '' }} />;
}

export function RenderBlock(props: RenderProps): ReactNode {
  const { block, mode, onSelect, selectedId } = props;
  const def = getDefinition(block.type);
  if (!def) {
    return (
      <div data-block-id={block.id} className="cb-block cb-unknown">
        Unknown component <code>{block.type}</code>
      </div>
    );
  }

  const selAttrs = selectionAttrs(block, mode, selectedId);
  const handleClick = (e: React.MouseEvent) => {
    if (mode !== 'edit' || !onSelect) return;
    e.stopPropagation();
    onSelect(block.id);
  };

  /* Helper used by all rich blocks. */
  const richSlot = (key: string, html: string) => {
    if (mode === 'edit' && props.renderRich) return props.renderRich(block, key, html);
    return <NoOpRich html={html} />;
  };

  switch (block.type) {
    /* ----- Headings ----- */
    case 'doc-header': {
      const brand = getProp(block, 'brand', 'Classroom.');
      const kicker = getProp(block, 'kicker', 'Worksheet');
      const m1 = getProp(block, 'metaLine1', '');
      const m2 = getProp(block, 'metaLine2', '');
      return (
        <header {...selAttrs} onClick={handleClick}>
          <div className="doc-header">
            <div className="brand">
              {brand} <span className="brand-dot">.</span> {kicker}
            </div>
            <div className="doc-meta">
              {m1 && <div>{m1}</div>}
              {m2 && <div>{m2}</div>}
            </div>
          </div>
        </header>
      );
    }
    case 'eyebrow': {
      const text = getProp(block, 'text', '');
      return (
        <div {...selAttrs} onClick={handleClick}>
          <div className="eyebrow">{text}</div>
        </div>
      );
    }
    case 'hero': {
      const title = getProp(block, 'title', '');
      const showStamp = getProp(block, 'showStamp', true);
      const stampValue = getProp(block, 'stampValue', '');
      const stampLabel = getProp(block, 'stampLabel', 'Total Points');
      return (
        <section {...selAttrs} onClick={handleClick}>
          <div className="hero">
            <h1>{title}</h1>
            {showStamp && (
              <div className="hero-stamp">
                <div className="num">{stampValue}</div>
                <div className="lbl">{stampLabel}</div>
              </div>
            )}
          </div>
        </section>
      );
    }
    case 'name-bar': {
      const fields = String(getProp(block, 'fields', 'Name,Date,Period,Score'))
        .split(',').map((s) => s.trim()).filter(Boolean);
      const showScore = getProp(block, 'showScore', true);
      const scoreOutOf = getProp(block, 'scoreOutOf', 20);
      return (
        <div {...selAttrs} onClick={handleClick}>
          <div className="name-bar">
            {fields.map((f, i) => (
              <div className="name-bar-field" key={`${f}-${i}`}>
                <span className="name-bar-label">{f}</span>
                <span className="name-bar-line" />
              </div>
            ))}
            {showScore && (
              <div className="name-bar-score">
                <span className="name-bar-line" />/{scoreOutOf}
              </div>
            )}
          </div>
        </div>
      );
    }
    case 'section-heading': {
      const letter = getProp(block, 'letter', '');
      const title = getProp(block, 'title', '');
      const points = getProp(block, 'points', '');
      return (
        <div {...selAttrs} onClick={handleClick}>
          <div className="section-heading">
            <span className="section-letter">Part {letter}</span>
            <h2 className="section-title">{title}</h2>
            {points && <span className="section-points">{points}</span>}
          </div>
        </div>
      );
    }

    /* ----- Text ----- */
    case 'paragraph': {
      const html = getProp(block, 'html', '');
      return (
        <div {...selAttrs} onClick={handleClick}>
          <div className="rich">{richSlot('html', html)}</div>
        </div>
      );
    }
    case 'directions': {
      const label = getProp(block, 'label', 'Directions:');
      const html = getProp(block, 'html', '');
      return (
        <div {...selAttrs} onClick={handleClick}>
          <div className="callout-box callout-soft">
            <div className="callout-body">
              <strong className="callout-lead">{label} </strong>
              <span className="rich rich-inline">{richSlot('html', html)}</span>
            </div>
          </div>
        </div>
      );
    }
    case 'callout': {
      const tone = getProp(block, 'tone', 'accent');
      const html = getProp(block, 'html', '');
      return (
        <div {...selAttrs} onClick={handleClick}>
          <div className={`callout-box callout-${tone}`}>
            <div className="rich">{richSlot('html', html)}</div>
          </div>
        </div>
      );
    }

    /* ----- Layout ----- */
    case 'spacer': {
      const h = getProp(block, 'height', 18);
      return <div {...selAttrs} onClick={handleClick} style={{ height: `${h}pt` }} />;
    }
    case 'divider': {
      const thickness = getProp(block, 'thickness', 'thin');
      return (
        <div {...selAttrs} onClick={handleClick}>
          <hr className={`composer-divider divider-${thickness}`} />
        </div>
      );
    }
    case 'work-area': {
      const lines = getProp(block, 'lines', 4);
      const height = getProp(block, 'height', 100);
      const label = getProp(block, 'label', 'Answer');
      return (
        <div {...selAttrs} onClick={handleClick}>
          <div className="work-area" style={{ minHeight: `${height}pt` }}>
            {Array.from({ length: lines }).map((_, i) => (
              <div className="work-area-line" key={i} />
            ))}
            {label && <div className="work-area-label">{label}</div>}
          </div>
        </div>
      );
    }
    case 'columns': {
      const cols = Number(getProp(block, 'columns', 2));
      const gap = Number(getProp(block, 'gap', 16));
      const style: CSSProperties = {
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: `${gap}pt`,
      };
      return (
        <div {...selAttrs} onClick={handleClick} style={style}>
          {(block.children || []).map((child) => (
            <RenderBlock key={child.id} {...props} block={child} />
          ))}
        </div>
      );
    }

    /* ----- Lists ----- */
    case 'question-list': {
      const start = Number(getProp(block, 'startAt', 1));
      return (
        <ol {...selAttrs} onClick={handleClick} className="q-list" start={start}>
          {(block.children || []).map((child) => (
            <RenderBlock key={child.id} {...props} block={child} />
          ))}
        </ol>
      );
    }
    case 'question': {
      const promptHtml = getProp<string>(block, 'prompt', '');
      const style = String(getProp<string>(block, 'answerStyle', 'line'));
      const answerLines = Number(getProp(block, 'answerLines', 3));
      return (
        <li {...selAttrs} onClick={handleClick} className="q-item">
          <div className="q-prompt rich">{richSlot('prompt', promptHtml)}</div>
          {style === 'line' && <div className="q-answer-line" />}
          {style === 'lines' && (
            <div className="q-answer-lines">
              {Array.from({ length: answerLines }).map((_, i) => (
                <div className="q-answer-line" key={i} />
              ))}
            </div>
          )}
          {style === 'box' && <div className="q-answer-box" />}
        </li>
      );
    }
    case 'choice-list': {
      const cols = Number(getProp(block, 'columns', 2));
      return (
        <ol
          {...selAttrs}
          onClick={handleClick}
          className="choice-list"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: '6pt',
          }}
        >
          {(block.children || []).map((child) => (
            <RenderBlock key={child.id} {...props} block={child} />
          ))}
        </ol>
      );
    }
    case 'choice': {
      const label = getProp(block, 'label', '');
      const correct = getProp(block, 'correct', false);
      return (
        <li {...selAttrs} onClick={handleClick} className={`choice ${correct ? 'is-correct' : ''}`}>
          <span className="choice-bubble">○</span>
          <span className="choice-label">{label}</span>
        </li>
      );
    }

    /* ----- Cards ----- */
    case 'card-grid': {
      const cols = Number(getProp(block, 'columns', 2));
      const cardHeight = Number(getProp(block, 'cardHeight', 140));
      return (
        <div
          {...selAttrs}
          onClick={handleClick}
          className="card-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: '8pt',
            ['--card-h' as never]: `${cardHeight}pt`,
          }}
        >
          {(block.children || []).map((child) => (
            <RenderBlock key={child.id} {...props} block={child} />
          ))}
        </div>
      );
    }
    case 'card': {
      const tag = getProp(block, 'tag', '');
      const title = getProp(block, 'title', '');
      const body = getProp(block, 'body', '');
      const foot = getProp(block, 'foot', '');
      return (
        <div {...selAttrs} onClick={handleClick} className="card" style={{ minHeight: 'var(--card-h, 140pt)' }}>
          <div className="card-tag">{tag}</div>
          <div className="card-title">{title}</div>
          <div className="card-body rich">{richSlot('body', body)}</div>
          <div className="card-foot">{foot}</div>
        </div>
      );
    }
    case 'bingo-grid': {
      const size = Number(getProp(block, 'size', 5));
      const free = getProp(block, 'freeCenter', true);
      const cells = String(getProp(block, 'cells', '')).split(',').map((s) => s.trim());
      const total = size * size;
      const grid: string[] = [];
      let i = 0;
      for (let r = 0; r < size; r += 1) {
        for (let c = 0; c < size; c += 1) {
          const isCenter = free && r === Math.floor(size / 2) && c === Math.floor(size / 2);
          if (isCenter) grid.push('FREE');
          else {
            grid.push(cells[i] || '');
            i += 1;
          }
        }
      }
      void total;
      return (
        <div
          {...selAttrs}
          onClick={handleClick}
          className="bingo"
          style={{ display: 'grid', gridTemplateColumns: `repeat(${size}, 1fr)`, gap: '4pt' }}
        >
          {grid.map((cell, idx) => (
            <div key={idx} className={`bingo-cell ${cell === 'FREE' ? 'is-free' : ''}`}>
              {cell}
            </div>
          ))}
        </div>
      );
    }

    /* ----- Assessment ----- */
    case 'rubric': {
      const scale = String(getProp(block, 'scale', '')).split(',').map((s) => s.trim());
      return (
        <div {...selAttrs} onClick={handleClick} className="rubric">
          <div
            className="rubric-row rubric-head"
            style={{ display: 'grid', gridTemplateColumns: `1.5fr repeat(${scale.length}, 1fr)`, gap: '4pt' }}
          >
            <div>Criterion</div>
            {scale.map((s, i) => <div key={i}>{s}</div>)}
          </div>
          {(block.children || []).map((child) => (
            <RenderBlock key={child.id} {...props} block={child} />
          ))}
        </div>
      );
    }
    case 'rubric-row': {
      const c = getProp(block, 'criterion', '');
      const d4 = getProp(block, 'd4', '');
      const d3 = getProp(block, 'd3', '');
      const d2 = getProp(block, 'd2', '');
      const d1 = getProp(block, 'd1', '');
      return (
        <div
          {...selAttrs}
          onClick={handleClick}
          className="rubric-row"
          style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr', gap: '4pt' }}
        >
          <div className="rubric-crit">{c}</div>
          <div>{d4}</div>
          <div>{d3}</div>
          <div>{d2}</div>
          <div>{d1}</div>
        </div>
      );
    }
    case 'agenda': {
      return (
        <div {...selAttrs} onClick={handleClick} className="agenda">
          {(block.children || []).map((child) => (
            <RenderBlock key={child.id} {...props} block={child} />
          ))}
        </div>
      );
    }
    case 'agenda-row': {
      const time = getProp(block, 'time', '');
      const name = getProp(block, 'name', '');
      const desc = getProp(block, 'desc', '');
      return (
        <div {...selAttrs} onClick={handleClick} className="agenda-row">
          <div className="agenda-time">{time}</div>
          <div className="agenda-step">
            <div className="agenda-name">{name}</div>
            <div className="agenda-desc">{desc}</div>
          </div>
        </div>
      );
    }

    default:
      return (
        <div {...selAttrs} onClick={handleClick}>
          <em>Renderer missing for {block.type}</em>
        </div>
      );
  }
}
